import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { useGetKsefIncomingStatusQuery, useRetryFailedKsefIncomingMutation } from '../Api/ksefIncomingApi';
import { KsefIncomingFailureDto, KsefLastExportJobDto, RetryFailedKsefIncomingResult } from '../Types/ksefIncoming.types';
import { formatKsefDateTime, getKsefExportJobStatusColor } from '../Utils/ksefIncomingStatus.utils';

interface CountBadgeProps {
  label: string;
  count: number;
  color?: 'primary' | 'warning' | 'error' | 'success' | 'default';
}

const CountBadge: React.FC<CountBadgeProps> = ({ label, count, color = 'default' }) => (
  <Box textAlign="center">
    <Typography variant="h5" fontWeight="bold">
      {count}
    </Typography>
    <Chip label={label} color={color} size="small" />
  </Box>
);

interface LastExportJobSectionProps {
  lastExportJob: KsefLastExportJobDto | null;
}

const LastExportJobSection: React.FC<LastExportJobSectionProps> = ({ lastExportJob }) => {
  if (!lastExportJob) {
    return (
      <Typography variant="body2" color="text.secondary">
        Brak zarejestrowanych jobów eksportu.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Status:
        </Typography>
        <Chip
          label={lastExportJob.status}
          color={getKsefExportJobStatusColor(lastExportJob.status)}
          size="small"
        />
        <Typography variant="body2" color="text.secondary">
          Faktury: {lastExportJob.invoiceCount} (nowe: {lastExportJob.newInvoiceCount})
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Start: {formatKsefDateTime(lastExportJob.startedAt)}
        {lastExportJob.completedAt && ` | Koniec: ${formatKsefDateTime(lastExportJob.completedAt)}`}
      </Typography>
      {lastExportJob.hwmDate && (
        <Typography variant="body2" color="text.secondary">
          HWM: {formatKsefDateTime(lastExportJob.hwmDate)}
        </Typography>
      )}
      {lastExportJob.errorMessage && (
        <Alert severity="error" sx={{ py: 0 }}>
          {lastExportJob.errorMessage}
        </Alert>
      )}
    </Stack>
  );
};

interface RecentFailuresTableProps {
  failures: KsefIncomingFailureDto[];
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: () => void;
}

const RecentFailuresTable: React.FC<RecentFailuresTableProps> = ({
  failures,
  selectedIds,
  onToggle,
  onToggleAll,
}) => {
  if (failures.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Brak błędnych faktur.
      </Typography>
    );
  }

  const allSelected = failures.length > 0 && selectedIds.size === failures.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < failures.length;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={onToggleAll}
              inputProps={{ 'aria-label': 'Zaznacz wszystkie' }}
            />
          </TableCell>
          <TableCell>ID</TableCell>
          <TableCell>Nr KSeF</TableCell>
          <TableCell>NIP sprzedawcy</TableCell>
          <TableCell>Data wystawienia</TableCell>
          <TableCell>Typ podmiotu</TableCell>
          <TableCell>Pobrano</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {failures.map((f) => (
          <TableRow key={f.id} selected={selectedIds.has(f.id)}>
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={selectedIds.has(f.id)}
                onChange={() => onToggle(f.id)}
                inputProps={{ 'aria-label': `Zaznacz fakturę ${f.id}` }}
              />
            </TableCell>
            <TableCell>{f.id}</TableCell>
            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{f.ksefNumber}</TableCell>
            <TableCell>{f.sellerNip ?? '—'}</TableCell>
            <TableCell>{f.issueDate ?? '—'}</TableCell>
            <TableCell>{f.subjectType}</TableCell>
            <TableCell>{formatKsefDateTime(f.retrievedAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

interface KsefIncomingStatusWidgetProps {
  failedLimit?: number;
  pollingIntervalMs?: number;
}

const KsefIncomingStatusWidget: React.FC<KsefIncomingStatusWidgetProps> = ({
  failedLimit = 20,
  pollingIntervalMs,
}) => {
  const { data, isLoading, isError, error } = useGetKsefIncomingStatusQuery(
    { failedLimit },
    { pollingInterval: pollingIntervalMs }
  );

  const [retryFailedKsefIncoming, { isLoading: isRetrying }] = useRetryFailedKsefIncomingMutation();

  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set());
  const [retryResult, setRetryResult] = React.useState<RetryFailedKsefIncomingResult | null>(null);

  const handleToggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    if (!data) return;
    const allIds = data.recentFailures.map((f) => f.id);
    setSelectedIds((prev) =>
      prev.size === allIds.length ? new Set() : new Set(allIds)
    );
  };

  const handleRetry = async () => {
    if (selectedIds.size === 0) return;
    try {
      const result = await retryFailedKsefIncoming({
        incomingInvoiceIds: Array.from(selectedIds),
      }).unwrap();
      setRetryResult(result);
      setSelectedIds(new Set());
    } catch {
      setRetryResult(null);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap={1} p={2}>
        <CircularProgress size={20} />
        <Typography variant="body2">Ładowanie statusu faktur przychodzących KSeF…</Typography>
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Alert severity="error">
        Błąd ładowania statusu faktur przychodzących KSeF.{' '}
        {extractApiErrorMessage(error, 'Wystąpił nieoczekiwany błąd.')}
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        KSeF — Faktury przychodzące
      </Typography>

      {/* Liczniki statusów */}
      <Stack direction="row" spacing={4} justifyContent="center" mb={2}>
        <CountBadge label="Oczekujące" count={data.counts.pending} color="default" />
        <CountBadge label="Przetwarzane" count={data.counts.processing} color="warning" />
        <CountBadge label="Błędne" count={data.counts.failed} color="error" />
        <CountBadge label="Zaimportowane" count={data.counts.imported} color="success" />
        <CountBadge label="Łącznie" count={data.counts.total} />
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Ostatni job eksportu */}
      <Typography variant="subtitle2" gutterBottom>
        Ostatni job eksportu
      </Typography>
      <LastExportJobSection lastExportJob={data.lastExportJob} />

      <Divider sx={{ my: 2 }} />

      {/* Lista błędnych faktur */}
      <Typography variant="subtitle2" gutterBottom>
        Ostatnie błędne faktury ({data.recentFailures.length})
      </Typography>
      <RecentFailuresTable
        failures={data.recentFailures}
        selectedIds={selectedIds}
        onToggle={handleToggle}
        onToggleAll={handleToggleAll}
      />

      {data.recentFailures.length > 0 && (
        <Box mt={1} display="flex" alignItems="center" gap={2}>
          <Button
            variant="contained"
            color="warning"
            size="small"
            disabled={selectedIds.size === 0 || isRetrying}
            onClick={handleRetry}
          >
            {isRetrying
              ? 'Przetwarzanie…'
              : `Ponów przetwarzanie zaznaczonych (${selectedIds.size})`}
          </Button>
          {retryResult !== null && (
            <Alert severity="success" sx={{ py: 0, flex: 1 }}>
              Zresetowano: {retryResult.resetCount}, pominięto: {retryResult.skippedCount}
            </Alert>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default KsefIncomingStatusWidget;
